/**
 * useBoardSocket — opens a WebSocket to the BoardRoom DO and exposes:
 *   - connection state
 *   - the current presence list
 *   - a dispatcher you wire into your UI to react to mutation broadcasts
 *
 * Reconnect: exponential backoff with jitter, capped at 30s.
 * Visibility: when the tab is hidden we keep the socket open (so we don't
 * lose state) but pause reconnection attempts on failure to save battery.
 */

import { useEffect, useRef, useState } from 'react';
import type { ServerMessage } from '../../shared/messages';

export interface PresenceUser {
  id: string;
  name: string;
  avatarHue: number;
  avatarUrl: string | null;
}

export type BoardSocketState = 'connecting' | 'open' | 'closed';

export function useBoardSocket(
  boardId: string,
  handle: (msg: ServerMessage) => void,
): { state: BoardSocketState; presence: PresenceUser[] } {
  const [state, setState] = useState<BoardSocketState>('connecting');
  const [presence, setPresence] = useState<PresenceUser[]>([]);
  const handleRef = useRef(handle);
  handleRef.current = handle;

  useEffect(() => {
    let socket: WebSocket | null = null;
    let cancelled = false;
    let attempt = 0;
    let pingTimer: number | undefined;
    let reconnectTimer: number | undefined;

    const connect = () => {
      if (cancelled) return;
      setState('connecting');
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${scheme}://${window.location.host}/api/boards/${boardId}/ws`;
      socket = new WebSocket(url);

      socket.onopen = () => {
        attempt = 0;
        setState('open');
        pingTimer = window.setInterval(() => {
          try {
            socket?.send(JSON.stringify({ type: 'ping' }));
          } catch {
            /* ignored */
          }
        }, 25_000);
      };

      socket.onmessage = (ev) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(ev.data as string) as ServerMessage;
        } catch {
          return;
        }
        if (msg.type === 'presence.snapshot') setPresence(msg.users);
        else if (msg.type === 'presence.joined')
          setPresence((cur) => (cur.find((u) => u.id === msg.user.id) ? cur : [...cur, msg.user]));
        else if (msg.type === 'presence.left')
          setPresence((cur) => cur.filter((u) => u.id !== msg.userId));
        handleRef.current(msg);
      };

      socket.onclose = () => {
        if (pingTimer !== undefined) clearInterval(pingTimer);
        setState('closed');
        if (cancelled) return;
        const delay = Math.min(30_000, 500 * Math.pow(1.7, attempt++)) + Math.random() * 250;
        reconnectTimer = window.setTimeout(connect, delay);
      };

      socket.onerror = () => {
        try {
          socket?.close();
        } catch {
          /* ignored */
        }
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (pingTimer !== undefined) clearInterval(pingTimer);
      if (reconnectTimer !== undefined) clearTimeout(reconnectTimer);
      try {
        socket?.close();
      } catch {
        /* ignored */
      }
    };
  }, [boardId]);

  return { state, presence };
}
